import type { CaseDefinition, ScenarioDocument } from './types';
import casesData from '../data/cases.json';

type ScenarioLoader = () => Promise<ScenarioDocument>;

const CASES = [...(casesData as { cases: CaseDefinition[] }).cases].sort(
  (left, right) => left.displayOrder - right.displayOrder
);

const SCENARIO_LOADERS: Record<string, ScenarioLoader> = {
  case0: () => loadScenarioJson(() => import('../data/scenario/case0.json')),
  case1: () => loadScenarioJson(() => import('../data/scenario/case1.json')),
  case2: () => loadScenarioJson(() => import('../data/scenario/case2.json')),
  case3: () => loadScenarioJson(() => import('../data/scenario/case3.json')),
  case4: () => loadScenarioJson(() => import('../data/scenario/case4.json')),
  case5: () => loadScenarioJson(() => import('../data/scenario/case5.json')),
  case6: () => loadScenarioJson(() => import('../data/scenario/case6.json')),
  case7: () => loadScenarioJson(() => import('../data/scenario/case7.json')),
  case8: () => loadScenarioJson(() => import('../data/scenario/case8.json')),
  case9: () => loadScenarioJson(() => import('../data/scenario/case9.json')),
  case10: () => loadScenarioJson(() => import('../data/scenario/case10.json'))
};

export function listCases(): CaseDefinition[] {
  return CASES.map((caseDefinition) => ({ ...caseDefinition }));
}

export function findCaseDefinition(caseId: string): CaseDefinition | null {
  const caseDefinition = CASES.find((candidate) => candidate.id === caseId);
  return caseDefinition ? { ...caseDefinition } : null;
}

export function getInitialCaseDefinition(): CaseDefinition {
  const initialCase = CASES[0];
  if (!initialCase) {
    throw new Error('No cases are registered in src/data/cases.json.');
  }

  return { ...initialCase };
}

export function getNextCaseDefinition(caseId: string): CaseDefinition | null {
  const currentIndex = CASES.findIndex((caseDefinition) => caseDefinition.id === caseId);
  if (currentIndex < 0) {
    return null;
  }

  const nextCase = CASES[currentIndex + 1];
  return nextCase ? { ...nextCase } : null;
}

export function getCaseSaveTitle(caseId: string): string {
  const caseDefinition = findCaseDefinition(caseId);
  if (!caseDefinition) {
    return '事件ファイル';
  }

  return `Case ${caseDefinition.displayOrder} ${caseDefinition.title}`;
}

export function getCaseLabel(caseId: string): string {
  const caseDefinition = findCaseDefinition(caseId);
  return caseDefinition ? `Case ${caseDefinition.displayOrder}` : caseId;
}

export async function loadScenarioForCase(caseId: string): Promise<ScenarioDocument> {
  const loader = SCENARIO_LOADERS[caseId];
  if (!loader) {
    throw new Error(`Scenario loader for case "${caseId}" is not registered.`);
  }

  const scenario = await loader();
  if (scenario.caseId !== caseId) {
    throw new Error(
      `Scenario "${scenario.id}" has caseId "${scenario.caseId}", expected "${caseId}".`
    );
  }

  return scenario;
}

export async function loadScenarioForResume(
  caseId: string,
  scenarioId: string
): Promise<ScenarioDocument> {
  const scenario = await loadScenarioForCase(caseId);
  if (scenario.id === scenarioId || scenarioId === scenario.startNodeId) {
    return scenario;
  }

  const fallbackCase = CASES.find(
    (caseDefinition) =>
      caseDefinition.id === scenarioId || caseDefinition.firstScenarioId === scenarioId
  );
  if (!fallbackCase) {
    return scenario;
  }

  return loadScenarioForCase(fallbackCase.id);
}

async function loadScenarioJson(
  loader: () => Promise<{ default: unknown }>
): Promise<ScenarioDocument> {
  const module = await loader();
  return module.default as ScenarioDocument;
}
